import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { UserService } from './api/user/user.service';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import { ColumnOption, ColumnToggleOptions } from '@models/shared.model';
import { StationColumnConfig } from '@models/user/station-inbox-choices';
import {
    DiscrepancyViewColumnOptions,
    DocumentViewColumnOptions,
    DocumentViewNestedColumnOptions,
} from '@station/utils';

const STATION_CUSTOM_COLUMN_STORAGE_KEY = 'wfe.station.custom-hidden-columns';
const STATIC_DOCUMENT_COLUMN_IDS = new Set(
    DocumentViewColumnOptions.map((option) => String(option.id).toLowerCase()),
);
const STATIC_NESTED_COLUMN_IDS = new Set(
    DocumentViewNestedColumnOptions.map((option) =>
        String(option.id).toLowerCase(),
    ),
);
const STATIC_DISCREPANCY_COLUMN_IDS = new Set(
    DiscrepancyViewColumnOptions.map((option) =>
        String(option.id).toLowerCase(),
    ),
);

type StationCustomHiddenColumns = {
    documentView: string[];
    nestedDocumentView: string[];
    discrepancyView: string[];
};

@Injectable({
    providedIn: 'root',
})
export class StationColumnOptionsService {
    private readonly columnOptions = new BehaviorSubject<
        ColumnToggleOptions<string>
    >({
        lastToggled: null,
        hiddenColumns: [],
    });
    private readonly nestedColumnOptions = new BehaviorSubject<{
        hiddenColumns: string[];
    }>({ hiddenColumns: [] });
    private readonly discColumnOptions = new BehaviorSubject<
        ColumnToggleOptions<string>
    >({
        lastToggled: null,
        hiddenColumns: [],
    });

    columnOptions$ = this.columnOptions.asObservable();
    nestedColumnOptions$ = this.nestedColumnOptions.asObservable();
    discColumnOptions$ = this.discColumnOptions.asObservable();

    private readonly userService = inject(UserService);
    private _subscriptions = new SubscriptionList() as SubscriptionListType;

    updateColumnOptions(option: Pick<ColumnOption<string>, 'id' | 'checked'>) {
        this.columnOptions.next({
            lastToggled: option,
            hiddenColumns: this.computeHiddenColumns(
                this.columnOptions.value.hiddenColumns,
                option,
            ),
        });

        this.saveColumnOptions();
    }

    updateNestedColumnOptions(
        option: Pick<ColumnOption<string>, 'id' | 'checked'>,
    ) {
        this.nestedColumnOptions.next({
            hiddenColumns: this.computeHiddenColumns(
                this.nestedColumnOptions.value.hiddenColumns,
                option,
            ),
        });

        this.saveColumnOptions();
    }

    updateDiscColumnOptions(
        option: Pick<ColumnOption<string>, 'id' | 'checked'>,
    ) {
        this.discColumnOptions.next({
            lastToggled: option,
            hiddenColumns: this.computeHiddenColumns(
                this.discColumnOptions.value.hiddenColumns,
                option,
            ),
        });

        this.saveColumnOptions();
    }

    /**
     * Add or remove a column id from the hidden list based on the user's checked
     * state (true = show/remove, false = hide/add). Matching is case-insensitive
     * so camelCase option ids (e.g. 'documentType') always cancel out a restored
     * lowercased id, preventing duplicate entries that revert the column on
     * refresh and can trigger a backend unique-key (DbUpdateException) error.
     */
    private computeHiddenColumns(
        current: string[],
        option: Pick<ColumnOption<string>, 'id' | 'checked'>,
    ): string[] {
        const id = String(option.id).toLowerCase();
        const withoutId = current.filter((h) => h.toLowerCase() !== id);
        return option.checked ? withoutId : [...withoutId, option.id];
    }

    syncColumnConfig(config: StationColumnConfig) {
        this.columnOptions.next({
            lastToggled: null,
            hiddenColumns: this.mergeCustomHiddenColumns(
                config.documentView.hiddenColumns,
                'documentView',
            ),
        });
        this.nestedColumnOptions.next({
            hiddenColumns: this.mergeCustomHiddenColumns(
                config.documentView.nestedHiddenColumns,
                'nestedDocumentView',
            ),
        });
        this.discColumnOptions.next({
            lastToggled: null,
            hiddenColumns: this.mergeCustomHiddenColumns(
                config.discrepancyView.hiddenColumns,
                'discrepancyView',
            ),
        });
    }

    private saveColumnOptions() {
        const config: StationColumnConfig = {
            documentView: {
                hiddenColumns: this.columnOptions.getValue().hiddenColumns,
                nestedHiddenColumns:
                    this.nestedColumnOptions.getValue().hiddenColumns,
            },
            discrepancyView: {
                hiddenColumns: this.discColumnOptions.getValue().hiddenColumns,
            },
        };

        this.saveCustomHiddenColumns(config);

        this._subscriptions.unsubscribeSafe('save-station-column-config');
        this._subscriptions['save-station-column-config'] = this.userService
            .saveStationColumnConfig(config)
            .subscribe();
    }

    private mergeCustomHiddenColumns(
        serverHiddenColumns: string[],
        scope: keyof StationCustomHiddenColumns,
    ) {
        const localCustomHiddenColumns = this.readCustomHiddenColumns()[scope];
        return [
            ...new Set([
                ...(serverHiddenColumns ?? []),
                ...localCustomHiddenColumns,
            ]),
        ];
    }

    private saveCustomHiddenColumns(config: StationColumnConfig) {
        const payload: StationCustomHiddenColumns = {
            documentView: this.extractCustomHiddenColumns(
                config.documentView.hiddenColumns,
                STATIC_DOCUMENT_COLUMN_IDS,
            ),
            nestedDocumentView: this.extractCustomHiddenColumns(
                config.documentView.nestedHiddenColumns,
                STATIC_NESTED_COLUMN_IDS,
            ),
            discrepancyView: this.extractCustomHiddenColumns(
                config.discrepancyView.hiddenColumns,
                STATIC_DISCREPANCY_COLUMN_IDS,
            ),
        };

        try {
            globalThis.localStorage?.setItem(
                STATION_CUSTOM_COLUMN_STORAGE_KEY,
                JSON.stringify(payload),
            );
        } catch {
            // Ignore storage failures; server persistence still handles static columns.
        }
    }

    private readCustomHiddenColumns(): StationCustomHiddenColumns {
        try {
            const raw = globalThis.localStorage?.getItem(
                STATION_CUSTOM_COLUMN_STORAGE_KEY,
            );
            if (!raw) return this.emptyCustomHiddenColumns();

            const parsed = JSON.parse(
                raw,
            ) as Partial<StationCustomHiddenColumns>;
            return {
                documentView: Array.isArray(parsed.documentView)
                    ? parsed.documentView
                    : [],
                nestedDocumentView: Array.isArray(parsed.nestedDocumentView)
                    ? parsed.nestedDocumentView
                    : [],
                discrepancyView: Array.isArray(parsed.discrepancyView)
                    ? parsed.discrepancyView
                    : [],
            };
        } catch {
            return this.emptyCustomHiddenColumns();
        }
    }

    private extractCustomHiddenColumns(
        hiddenColumns: string[],
        staticColumnIds: ReadonlySet<string>,
    ) {
        return (hiddenColumns ?? []).filter(
            (id) => !staticColumnIds.has(String(id).toLowerCase()),
        );
    }

    private emptyCustomHiddenColumns(): StationCustomHiddenColumns {
        return {
            documentView: [],
            nestedDocumentView: [],
            discrepancyView: [],
        };
    }
}
